import type { ComponentType } from "react";
import AsesoresPanel from "@/components/configuracion/AsesoresPanel";
import CategoriasPanel from "@/components/configuracion/CategoriasPanel";
import ContactosPanel from "@/components/configuracion/ContactosPanel";
import MarcasPanel from "@/components/configuracion/MarcasPanel";
import ModelosPanel from "@/components/configuracion/ModelosPanel";
import PlantillasNotifPanel from "@/components/configuracion/PlantillasNotifPanel";
import ProveedoresPanel from "@/components/configuracion/ProveedoresPanel";
import TelegramAdminPanel from "@/components/configuracion/TelegramAdminPanel";
import TiposFallaPanel from "@/components/configuracion/TiposFallaPanel";
import TiposSolicitudPanel from "@/components/configuracion/TiposSolicitudPanel";
import TiposSolucionPanel from "@/components/configuracion/TiposSolucionPanel";
import SeccionLayout from "@/components/configuracion/SeccionLayout";
import { AYUDA_SECCION } from "@/components/ayuda/vistas/configuracion";
import AvisosProgramados from "@/components/configuracion/sistema/AvisosProgramados";
import CorreoEntrante from "@/components/configuracion/sistema/CorreoEntrante";
import Buzon from "./sistema/Buzon";
import DatosEmpresa from "@/components/configuracion/sistema/DatosEmpresa";
import IntegracionJimi from "@/components/configuracion/sistema/IntegracionJimi";
import IntegracionQueclink from "@/components/configuracion/sistema/IntegracionQueclink";
import LlaveClaude from "@/components/configuracion/sistema/LlaveClaude";
import RevisionIA from "@/components/configuracion/sistema/RevisionIA";
import TiemposEscalacion from "@/components/configuracion/sistema/TiemposEscalacion";

export type PropsSeccion = {
  /** Deep-link desde el detalle de un caso: enfoca a ese cliente. */
  focusClienteId?: number;
};

export type Seccion = {
  /** Slug de la URL (`?seccion=`). Es contrato: sale en enlaces guardados. */
  id: string;
  /** Etiqueta del menú. Corta: la explicación va en el encabezado. */
  titulo: string;
  Panel: ComponentType<PropsSeccion>;
};

export type GrupoSecciones = {
  titulo: string;
  secciones: Seccion[];
};

/**
 * Envuelve un panel heredado con el marco de sección.
 *
 * Convención: **cada sección se dibuja completa, con su propio encabezado**.
 * Las secciones nuevas (las de `sistema/`) ya lo traen; los paneles que existían
 * antes de este rediseño no, así que se les pone aquí en vez de tocar los once.
 * Se llama a nivel de módulo, nunca en render: si no, React vería un componente
 * distinto en cada pasada y remontaría el panel entero.
 */
function conMarco(
  titulo: string,
  descripcion: string,
  Panel: ComponentType<PropsSeccion>,
  idAyuda: string,
): ComponentType<PropsSeccion> {
  return function SeccionEnmarcada(props: PropsSeccion) {
    return (
      <SeccionLayout titulo={titulo} descripcion={descripcion} ayuda={AYUDA_SECCION[idAyuda]}>
        <Panel {...props} />
      </SeccionLayout>
    );
  };
}

/**
 * Las secciones de Configuración, agrupadas por lo que son.
 *
 * Agregar una sección es **una línea aquí**: el menú, el ruteo por `?seccion=` y
 * el título salen todos de este mapa. Antes había que tocar tres lugares (la
 * unión de tipos, el arreglo de válidos y una escalera de ternarios para la
 * etiqueta), y por eso la barra terminó con once pestañas y una de ellas
 * convertida en cajón de sastre.
 */
export const GRUPOS: GrupoSecciones[] = [
  {
    titulo: "Catálogos",
    secciones: [
      {
        id: "marcas",
        titulo: "Marcas",
        Panel: conMarco(
          "Marcas",
          "Las marcas de equipo que se pueden elegir al crear un caso. Si falta una, se puede agregar aquí o al vuelo desde «Nuevo caso». Para quitarla de la lista sin borrar su historial, desactívala.",
          MarcasPanel,
          "marcas",
        ),
      },
      {
        id: "modelos",
        titulo: "Modelos",
        Panel: conMarco(
          "Modelos",
          "Los modelos de cada marca. Renombrar un modelo aquí no cambia los casos viejos: cada caso guarda el texto con el que se creó.",
          ModelosPanel,
          "modelos",
        ),
      },
      {
        id: "categorias",
        titulo: "Categorías",
        Panel: conMarco(
          "Categorías",
          "En qué categoría cae un caso. Renombrarlas es seguro: los casos guardan una clave interna, no el nombre que se ve.",
          CategoriasPanel,
          "categorias",
        ),
      },
      {
        id: "tipos-solicitud",
        titulo: "Tipos de solicitud",
        Panel: conMarco(
          "Tipos de solicitud",
          "Qué se le está pidiendo al proveedor. Aparecen al crear un caso de la categoría «solicitud».",
          TiposSolicitudPanel,
          "tipos-solicitud",
        ),
      },
      {
        id: "tipos-falla",
        titulo: "Tipos de falla",
        Panel: conMarco(
          "Tipos de falla",
          "Cómo se clasifica lo que le pasa a un equipo. Aparecen al crear un caso de la categoría «falla» y alimentan la estadística de fallas recurrentes.",
          TiposFallaPanel,
          "tipos-falla",
        ),
      },
      {
        id: "tipos-solucion",
        titulo: "Tipos de solución",
        Panel: conMarco(
          "Tipos de solución",
          "Con qué se cierra un caso. Cerrar obliga a elegir uno, y de aquí sale la estadística de cómo se resuelven los casos.",
          TiposSolucionPanel,
          "tipos-solucion",
        ),
      },
    ],
  },
  {
    titulo: "Directorio",
    secciones: [
      {
        id: "contactos",
        titulo: "Clientes y contactos",
        Panel: conMarco(
          "Clientes y contactos",
          "Las empresas a las que damos soporte y las personas de cada una. Un contacto que se edita aquí queda actualizado en todos sus casos.",
          ContactosPanel,
          "contactos",
        ),
      },
      {
        id: "proveedores",
        titulo: "Proveedores",
        Panel: conMarco(
          "Proveedores",
          "A quién se le manda un RMA y con quién se habla ahí. El caso resuelve su proveedor por la marca del equipo, así que conviene que cada marca tenga el suyo.",
          ProveedoresPanel,
          "proveedores",
        ),
      },
      {
        id: "asesores",
        titulo: "Asesores comerciales",
        Panel: conMarco(
          "Asesores comerciales",
          "Quién lleva comercialmente a cada empresa. El caso hereda el asesor de su cliente y lo ofrece con un clic para copia en los correos.",
          AsesoresPanel,
          "asesores",
        ),
      },
    ],
  },
  {
    titulo: "Automatizaciones",
    secciones: [
      { id: "tiempos", titulo: "Tiempos y escalación", Panel: TiemposEscalacion },
      { id: "avisos", titulo: "Avisos programados", Panel: AvisosProgramados },
      { id: "revision-ia", titulo: "Revisión con IA", Panel: RevisionIA },
    ],
  },
  {
    titulo: "Integraciones",
    secciones: [
      { id: "jimi", titulo: "Tickets de Jimi", Panel: IntegracionJimi },
      { id: "queclink", titulo: "Tickets de Queclink", Panel: IntegracionQueclink },
      {
        id: "llave-claude",
        titulo: "Llave de Claude",
        Panel: conMarco(
          "Llave de Claude",
          "La llave con la que la app le habla a Claude. De ella dependen la revisión de la bandeja con IA, el resumen ejecutivo, los comentarios de correo, el análisis de la sala de cierre y la traducción del chino de los tickets de Jimi. Rotarla aquí surte efecto al momento: el servicio web la toma enseguida y el worker en su siguiente vuelta, sin reiniciar nada.",
          LlaveClaude,
          "llave-claude",
        ),
      },
      {
        id: "telegram",
        titulo: "Bot de Telegram",
        Panel: conMarco(
          "Bot de Telegram",
          "El bot manda recordatorios y avisos a quien lo tenga vinculado desde su perfil. Al guardar un token se valida con Telegram, se guarda cifrado y se registra el webhook en el mismo paso. Rotar el token del mismo bot conserva las vinculaciones; cambiar a otro bot obliga a que cada quien vuelva a vincularse.",
          TelegramAdminPanel,
          "telegram",
        ),
      },
    ],
  },
  {
    titulo: "Correo y empresa",
    secciones: [
      { id: "correo-entrante", titulo: "Correo entrante", Panel: CorreoEntrante },
      // Va justo después: leídas en fila cuentan una historia — qué correo
      // entra, y qué correo se revisa.
      { id: "buzon", titulo: "Buzón", Panel: Buzon },
      { id: "empresa", titulo: "Datos de la empresa", Panel: DatosEmpresa },
      {
        id: "mensajes-rapidos",
        titulo: "Mensajes rápidos",
        Panel: conMarco(
          "Mensajes rápidos",
          "Las frases de un clic del recuadro «Pasar a un compañero», para no teclear lo mismo cada vez.",
          PlantillasNotifPanel,
          "mensajes-rapidos",
        ),
      },
    ],
  },
];

export const SECCIONES: Seccion[] = GRUPOS.flatMap((g) => g.secciones);

export const SECCION_INICIAL = SECCIONES[0].id;

export function buscarSeccion(id: string | null): Seccion | undefined {
  return SECCIONES.find((s) => s.id === id);
}

/**
 * Traducción de las pestañas viejas (`?tab=`) a las secciones nuevas.
 *
 * Ningún componente genera ya esos enlaces, pero pueden estar guardados en un
 * marcador o pegados en una conversación. La pestaña «notificaciones» nunca
 * configuró notificaciones —eran las frases del recuadro «Pasar a un
 * compañero»—, así que aterriza en «Mensajes rápidos», que sí dice lo que es.
 * «sistema» era el cajón de sastre; se manda a la primera de sus secciones.
 */
export const SECCION_POR_TAB_VIEJO: Record<string, string> = {
  marcas: "marcas",
  modelos: "modelos",
  proveedores: "proveedores",
  asesores: "asesores",
  categorias: "categorias",
  "tipos-solicitud": "tipos-solicitud",
  "tipos-falla": "tipos-falla",
  "tipos-solucion": "tipos-solucion",
  contactos: "contactos",
  notificaciones: "mensajes-rapidos",
  sistema: "tiempos",
};
